/**
 * DJI SRT importer.
 *
 * Parses the per-frame subtitle file DJI drones write next to footage and
 * produces the raw side of the Canonical Flight Model. Derived channels are
 * added later by the derive engine; this module only reads what is in the file.
 *
 * Observed Mini 5 Pro entry shape:
 *
 *   1
 *   00:00:00,000 --> 00:00:00,016
 *   <font size="28">FrameCnt: 1, DiffTime: 16ms
 *   2026-06-26 10:16:22.077
 *   [iso: 100] [shutter: 1/120.0] [fnum: 1.8] [ev: 0.3] [color_md: dlog_m]
 *   [focal_len: 24.00] [latitude: 40.034032] [longitude: 32.857478]
 *   [rel_alt: 0.000 abs_alt: 1197.332] [ct: 5600] </font>
 *
 * Field names and bracket layout vary across DJI models/firmware, so we parse
 * defensively by key rather than by fixed position.
 */

import type {
  ChannelDef,
  Flight,
  FlightSource,
  GeoPoint,
  SampleStore,
} from '../model/types.ts';
import { gpsValid } from '../geo/geo.ts';

export const IMPORTER_VERSION = '0.1.0';

/** Numeric bracket fields we recognize, with their canonical channel metadata. */
interface RawSpec {
  /** regex capture for the value, run against the whole bracket block */
  re: RegExp;
  key: string;
  label: string;
  unit: string;
  kind: ChannelDef['kind'];
}

const NUMERIC_SPECS: RawSpec[] = [
  // DiffTime = ms since the previous OSD frame (logging cadence, ~16ms @60fps).
  // Spikes above the nominal interval mark dropped/stuttered frames — not RC↔drone
  // signal latency (that metric isn't in the SRT).
  { re: /\bDiffTime:\s*([\d.]+)/, key: 'frame_interval', label: 'Frame interval', unit: 'ms', kind: 'scalar' },
  { re: /\biso:\s*([\d.]+)/, key: 'iso', label: 'ISO', unit: '', kind: 'scalar' },
  { re: /\bev:\s*(-?[\d.]+)/, key: 'ev', label: 'EV', unit: '', kind: 'scalar' },
  { re: /\bfnum:\s*([\d.]+)/, key: 'fnum', label: 'Aperture', unit: 'f', kind: 'scalar' },
  { re: /\bfocal_len:\s*([\d.]+)/, key: 'focal_len', label: 'Focal length', unit: 'mm', kind: 'scalar' },
  { re: /\bct:\s*([\d.]+)/, key: 'color_temp', label: 'Color temp', unit: 'K', kind: 'scalar' },
  { re: /\blatitude:\s*(-?[\d.]+)/, key: 'latitude', label: 'Latitude', unit: '°', kind: 'position' },
  { re: /\blongitude:\s*(-?[\d.]+)/, key: 'longitude', label: 'Longitude', unit: '°', kind: 'position' },
  { re: /\brel_alt:\s*(-?[\d.]+)/, key: 'rel_alt', label: 'Relative altitude', unit: 'm', kind: 'scalar' },
  { re: /\babs_alt:\s*(-?[\d.]+)/, key: 'abs_alt', label: 'Absolute altitude', unit: 'm', kind: 'scalar' },
];

/** Shutter is stored as "1/120.0" — convert to a numeric exposure time (s). */
const SHUTTER_RE = /\bshutter:\s*([\d.]+)\/([\d.]+)/;
/** color_md is categorical (e.g. "dlog_m"). */
const COLOR_MD_RE = /\bcolor_md:\s*(\w+)/;

const TIME_RE = /(\d{2}):(\d{2}):(\d{2}),(\d{3})\s*-->/;
const WALLCLOCK_RE = /(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?/;

function srtTimeToMs(h: string, m: string, s: string, ms: string): number {
  return ((+h * 60 + +m) * 60 + +s) * 1000 + +ms;
}

/**
 * Turn the SRT cue's wall clock into `Flight.startedAt` — a genuine UTC instant, as types.ts declares.
 *
 * The cue carries the CAMERA'S LOCAL time with no zone marker, so it is parsed as local (matching
 * `djiLog.startIso()`; correct whenever footage and viewer share a timezone, the assumption
 * lib/media/timecode.ts already documents).
 *
 * This used to be `` `${firstWall}Z` ``, which asserted that local digits were UTC. Downstream,
 * archiveScan's `wallClock()` renders the instant back to local, so the value got shifted TWICE by
 * the machine's UTC offset: a clip shot at 15:30 was stored and shown as 18:30 in Turkey (+3 h). That
 * surfaced as afternoon and sunset flights being classified `night` by the sun-phase filter; 588 of
 * 653 flights in the reference archive were wrong — every SRT-sourced one.
 * See notes/timestamp-tz-double-shift.md.
 *
 * WALLCLOCK_RE only pins digit COUNTS, so a corrupt cue can yield an out-of-range date ("2025-99-24").
 * Fall back to the epoch there, exactly as a missing wall clock does, rather than letting
 * `toISOString()` throw and take the whole import down.
 */
function startedAtFromWallClock(firstWall: string | null): string {
  if (firstWall) {
    const ms = Date.parse(firstWall); // no zone suffix ⇒ parsed as local time
    if (Number.isFinite(ms)) return new Date(ms).toISOString();
  }
  return new Date(0).toISOString();
}

export interface ParseResult {
  flight: Flight;
  /** Non-fatal issues worth surfacing (skipped blocks, missing GPS, etc.). */
  warnings: string[];
}

/**
 * Parse a DJI SRT string into a Flight carrying only raw channels.
 * @param text  full SRT file contents
 * @param sourceName  filename, used for the flight id and model hinting
 */
export function parseDjiSrt(text: string, sourceName = 'flight'): ParseResult {
  const warnings: string[] = [];
  // SRT blocks are separated by blank lines. Normalize newlines first.
  const blocks = text.replace(/\r\n/g, '\n').trim().split(/\n\s*\n/);

  const times: number[] = [];
  const cols: Record<string, number[]> = {};
  const colorMd: string[] = [];
  let firstWall: string | null = null;
  let sawShutter = false;

  for (const spec of NUMERIC_SPECS) cols[spec.key] = [];
  cols['shutter'] = [];

  for (const block of blocks) {
    const tm = TIME_RE.exec(block);
    if (!tm) continue; // not a cue block
    const startMs = srtTimeToMs(tm[1], tm[2], tm[3], tm[4]);
    times.push(startMs);

    if (!firstWall) {
      const wc = WALLCLOCK_RE.exec(block);
      if (wc) {
        const frac = (wc[7] ?? '0').padEnd(3, '0').slice(0, 3);
        firstWall = `${wc[1]}-${wc[2]}-${wc[3]}T${wc[4]}:${wc[5]}:${wc[6]}.${frac}`;
      }
    }

    for (const spec of NUMERIC_SPECS) {
      const m = spec.re.exec(block);
      cols[spec.key].push(m ? parseFloat(m[1]) : NaN);
    }
    const sh = SHUTTER_RE.exec(block);
    if (sh) {
      const denom = parseFloat(sh[2]);
      cols['shutter'].push(denom ? parseFloat(sh[1]) / denom : NaN);
      sawShutter = true;
    } else {
      cols['shutter'].push(NaN);
    }
    const cm = COLOR_MD_RE.exec(block);
    colorMd.push(cm ? cm[1] : '');
  }

  const n = times.length;
  if (n === 0) throw new Error('No telemetry cues found — is this a DJI SRT file?');

  // Normalize time so the first sample is t0 = 0.
  const t0 = times[0];
  const time = new Float64Array(n);
  for (let i = 0; i < n; i++) time[i] = times[i] - t0;

  // Decide which raw channels actually carry data.
  const numeric: Record<string, Float64Array> = {};
  const channels: ChannelDef[] = [];

  const allSpecs: RawSpec[] = [
    ...NUMERIC_SPECS,
    { re: /x/, key: 'shutter', label: 'Shutter (exposure)', unit: 's', kind: 'scalar' },
  ];
  for (const spec of allSpecs) {
    const arr = Float64Array.from(cols[spec.key]);
    if (arr.every((v) => Number.isNaN(v))) {
      // All-NaN = the channel is either genuinely ABSENT or the SRT format DRIFTED (a field
      // we couldn't parse). Distinguish: if the field NAME appears in the raw text but no
      // value parsed, warn (H4) — otherwise a real gauge vanishes silently, the class of
      // bug this review was opened for. Genuinely-absent fields stay silent (no noise).
      const field = spec.re.source.match(/\\b(\w+)/)?.[1];
      if (field && field !== 'x' && new RegExp(`\\b${field}\\s*:`, 'i').test(text)) {
        warnings.push(`"${spec.label}" is present in the SRT but couldn't be read — the DJI field format may have changed.`);
      }
      continue;
    }
    numeric[spec.key] = arr;
    channels.push({
      key: spec.key,
      label: spec.label,
      unit: spec.unit,
      kind: spec.kind,
      origin: 'raw',
      provenance: 'srt',
      range: rangeOf(arr),
    });
  }

  const categories: Record<string, string[]> = {};
  if (colorMd.some((v) => v !== '')) {
    categories['color_md'] = colorMd;
    channels.push({
      key: 'color_md',
      label: 'Color mode',
      unit: '',
      kind: 'categorical',
      origin: 'raw',
      provenance: 'srt',
      range: null,
    });
  }

  const hasGps = !!numeric['latitude'] && !!numeric['longitude'];
  if (!hasGps) warnings.push('No GPS in this file — map, speed and heading will be unavailable.');
  if (!sawShutter) warnings.push('No shutter field found.');
  // H5: no wall-clock parsed → startedAt falls back to 1970 (below). Say so, rather than
  // silently showing a bogus date and mis-syncing video by timecode.
  if (!firstWall) warnings.push('No timestamp in the SRT — flight date is unknown, so video auto-sync by timecode won’t work.');

  const samples: SampleStore = { length: n, time, numeric, categories };

  // Home = the first REAL fix, not (0,0). Flights that launch in ATTI / before a
  // GPS lock start at null island; using that as origin would make every distance
  // ~5000 km. Scan for the first valid fix instead.
  let originLat = 0;
  let originLon = 0;
  let originAbs: number | undefined;
  if (hasGps) {
    const la = numeric['latitude'];
    const lo = numeric['longitude'];
    for (let i = 0; i < n; i++) {
      if (gpsValid(la[i], lo[i])) {
        originLat = la[i];
        originLon = lo[i];
        originAbs = numeric['abs_alt']?.[i];
        break;
      }
    }
    if (originLat === 0 && originLon === 0) warnings.push('No valid GPS fix in this flight (ATTI / no satellites).');
  }
  const origin: GeoPoint = { lat: originLat, lon: originLon, absAlt: originAbs };

  const source: FlightSource = {
    vendor: 'DJI',
    model: guessModel(sourceName),
    importer: 'dji-srt',
    importerVersion: IMPORTER_VERSION,
  };

  const flight: Flight = {
    id: makeId(sourceName, firstWall ?? '', n),
    source,
    origin,
    startedAt: startedAtFromWallClock(firstWall),
    durationMs: time[n - 1],
    channels,
    samples,
  };

  return { flight, warnings };
}

function rangeOf(arr: Float64Array): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i];
    if (Number.isNaN(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (min === Infinity) return { min: 0, max: 0 };
  return { min, max };
}

function guessModel(name: string): string {
  // DJI filenames don't encode the model; leave room for a future lookup.
  return /DJI_/i.test(name) ? 'DJI drone' : 'unknown';
}

function makeId(name: string, wall: string, n: number): string {
  // Stable-ish id without a crypto dependency: source + start + count.
  const base = `${name}|${wall}|${n}`;
  let h = 2166136261;
  for (let i = 0; i < base.length; i++) {
    h ^= base.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}
